/**
 * One question, asked once.
 *
 * This screen exists because a workspace has to belong to something, and in Clerk
 * that something is an organisation. A user does not need to know that — being
 * asked to "create an organisation" on your first visit to a document tool is
 * baffling — so the question is "What is your business called?" and the
 * organisation happens behind it.
 *
 * The three lines underneath say what the answer is used for, which is the whole
 * reason somebody hesitates over a field like this: they cannot tell whether it is
 * a display name or a commitment. It is a display name, it goes on the documents,
 * and it can be changed.
 *
 * Above the question, when there are any: the workspaces this sign-in already
 * belongs to or has been invited to. Somebody arrives here whenever no workspace is
 * *selected*, which is not the same as having none — an invitation accepted by
 * signing up selects nothing, and the sign-in is shared with the SDK's account page.
 * Without the list, a colleague who followed an invitation would be asked to name a
 * business and would create a second, empty workspace beside the one they were
 * invited to.
 */
import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api.js";
import { clerk } from "../clerk.js";
import { Button, Field, Note } from "../ui/index.js";

/**
 * The placement a sign-up arrived from, as the marketing site hands it over:
 * `?source=producthunt`. The sign-in form returns here with the query intact, so
 * it is still in the address when the business is named. Informative only — the
 * service drops anything that is not a short slug.
 */
function sourceFromAddress(): string | null {
  const source = new URLSearchParams(window.location.search).get("source")?.toLowerCase() ?? "";
  return /^[a-z0-9-]{1,40}$/.test(source) ? source : null;
}

interface Existing {
  id: string;
  name: string;
  /** Present for an invitation, which has to be accepted before it can be opened. */
  accept?: () => Promise<unknown>;
}

export function Welcome({ message, hint, onChosen }: { message: string; hint?: string; onChosen: (organisationId: string) => Promise<void> }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState<Existing[]>([]);
  const [opening, setOpening] = useState<string | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  // The only field on the screen, so the cursor belongs in it.
  useEffect(() => input.current?.focus(), []);

  useEffect(() => {
    const user = clerk()?.user;
    if (!user) return;
    let cancelled = false;
    const memberships: Existing[] = user.organizationMemberships.map((m) => ({ id: m.organization.id, name: m.organization.name }));
    setExisting(memberships);
    user
      .getOrganizationInvitations({ status: "pending" })
      .then(({ data }) => {
        if (cancelled) return;
        const invitations = data
          .filter((invitation) => !memberships.some((m) => m.id === invitation.publicOrganizationData.id))
          .map((invitation) => ({ id: invitation.publicOrganizationData.id, name: invitation.publicOrganizationData.name, accept: () => invitation.accept() }));
        setExisting([...invitations, ...memberships]);
      })
      .catch(() => {
        // Invitations are a convenience here; the switcher shows them too.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const open = async (choice: Existing) => {
    setOpening(choice.id);
    setOpenError(null);
    try {
      await choice.accept?.();
      await onChosen(choice.id);
    } catch {
      setOpening(null);
      setOpenError(`We couldn't open ${choice.name} just now. Try again in a moment.`);
    }
  };

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return setError("Your business needs a name — it goes on your documents.");
    setBusy(true);
    setError(null);
    try {
      const created = await api.createWorkspace(trimmed, sourceFromAddress());
      await onChosen(created.clerkOrgId);
    } catch (e) {
      setBusy(false);
      setError(e instanceof ApiError ? (e.hint ? `${e.message} ${e.hint}` : e.message) : "We couldn't set that up just now. Try again in a moment.");
    }
  };

  return (
    <main className="main">
      <div className="wrap--narrow stack" style={{ gap: 24, paddingTop: "clamp(20px, 6vh, 64px)" }}>
        <img src="/logo.svg" alt="BroadPaper" style={{ height: 26 }} />
        <div className="stack" style={{ gap: 10 }}>
          <h1>{message}</h1>
          {hint ? <p className="muted" style={{ fontSize: 17, maxWidth: "52ch" }}>{hint}</p> : null}
        </div>

        {existing.length ? (
          <div className="card">
            <div className="card__head">
              <strong>Workspaces you already have access to</strong>
            </div>
            <div className="card__body stack" style={{ gap: 10 }}>
              {existing.map((choice) => (
                <div key={choice.id} className="spread" style={{ gap: 12 }}>
                  <span className="truncate">
                    {choice.name}
                    {choice.accept ? <span className="small muted"> · you've been invited</span> : null}
                  </span>
                  <Button size="sm" {...(choice.accept ? { variant: "primary" as const } : {})} busy={opening === choice.id} disabled={opening !== null || busy} onClick={() => void open(choice)}>
                    {choice.accept ? "Join" : "Open"}
                  </Button>
                </div>
              ))}
              {openError ? (
                <p className="small" style={{ margin: 0, color: "var(--red-deep)" }}>
                  {openError}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}

        <form
          className="card"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="card__body stack" style={{ gap: 16 }}>
            <Field label="What is your business called?" id="business" {...(error ? { error } : { hint: "This is the name that appears on your documents. You can change it later." })}>
              <input
                id="business"
                ref={input}
                className="input input--lg"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (error) setError(null);
                }}
                placeholder="Aldermead Ltd"
                autoComplete="organization"
                maxLength={120}
                disabled={busy}
              />
            </Field>
          </div>
          <div className="card__foot row" style={{ justifyContent: "space-between", gap: 12 }}>
            <span className="small muted">Nothing to install. No card.</span>
            <Button type="submit" variant="primary" busy={busy} icon="chevron">
              {busy ? "Setting up…" : "Create my workspace"}
            </Button>
          </div>
        </form>

        <Note tone="info" title="What this does">
          It creates the space your data, your designs and your documents live in. Everything belongs to the business rather than to one sign-in, so
          colleagues can be invited from People without anything being moved.
        </Note>
      </div>
    </main>
  );
}
